<template>
  <div class="pt-grid-wrap">
    <!-- 발표자 미리보기 띠 (source language) -->
    <div v-if="showSpeakerStrip" class="pt-speaker-strip">
      <span class="pt-speaker-strip__label">{{ $t ? $t('발표자') : '발표자' }}</span>
      <span class="pt-speaker-strip__lang">{{ sourceLangLabel }}</span>
      <span class="pt-speaker-strip__text">{{ sourcePreview }}</span>
    </div>

    <!-- 격자 -->
    <div class="pt-grid" :style="gridStyle">
      <pt-cell
        v-for="cell in orderedCells"
        :key="cell.lang + '_' + cell.slot"
        :config="cell"
        :runtime="runtimeFor(cell.lang)"
        :font-scale-global="fontScaleGlobal"
        :show-model-badge="showModelBadge"
        :direction="direction"
      />
    </div>
  </div>
</template>

<script>
import PtCell from '@/components/ai-pt/PtCell.vue'

const LAYOUT_TEMPLATES = {
  auto: null,                                    // CSS auto-fit
  '1x2': { cols: 2, rows: 1 },
  '2x2': { cols: 2, rows: 2 },
  '2x3': { cols: 3, rows: 2 },
  '3x3': { cols: 3, rows: 3 },
  '3x2': { cols: 2, rows: 3 },
  '4x2': { cols: 2, rows: 4 },
}

const LANG_LABEL_SHORT = {
  ko: '한국어', en: 'English', ja: '日本語', zh: '中文',
  vi: 'Tiếng Việt', th: 'ไทย', id: 'Bahasa', uz: 'O‘zbekcha',
  ne: 'नेपाली', mn: 'Монгол', km: 'ភាសាខ្មែរ', my: 'မြန်မာ',
  ru: 'Русский', es: 'Español', fr: 'Français', de: 'Deutsch',
  ar: 'العربية', hi: 'हिन्दी', bn: 'বাংলা', pt: 'Português',
}

export default {
  name: 'PtGrid',
  components: { PtCell },
  props: {
    /** 격자 설정 배열 — [{ slot, lang, bg, fg, fontScale, label, flag }, ...] */
    cells: {
      type: Array,
      default: function () { return [] },
    },
    /** 컴포저블의 cellsByLang — { 'en': {text,...}, 'vi': {...} } */
    cellsByLang: {
      type: Object,
      default: function () { return {} },
    },
    /** 'auto' | '1x2' | '2x2' | '2x3' | '3x3' ... */
    layout: {
      type: String,
      default: 'auto',
    },
    /** 발표자 미리보기 띠 표시 여부 */
    showSpeakerStrip: {
      type: Boolean,
      default: true,
    },
    /** 발표자 partial STT */
    sourcePreview: {
      type: String,
      default: '',
    },
    /** 발표자 언어 */
    sourceLang: {
      type: String,
      default: 'ko',
    },
    /** 글로벌 폰트 스케일 */
    fontScaleGlobal: {
      type: Number,
      default: 1.0,
    },
    showModelBadge: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'newest_top',
    },
  },
  computed: {
    orderedCells: function () {
      const arr = (this.cells || []).slice()
      arr.sort(function (a, b) {
        const sa = a && a.slot !== undefined ? a.slot : 0
        const sb = b && b.slot !== undefined ? b.slot : 0
        return sa - sb
      })
      return arr
    },
    sourceLangLabel: function () {
      return LANG_LABEL_SHORT[this.sourceLang] ? LANG_LABEL_SHORT[this.sourceLang] : this.sourceLang
    },
    gridStyle: function () {
      const template = LAYOUT_TEMPLATES[this.layout]
      if (!template) {
        // auto: CSS Grid auto-fit
        return {
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gridAutoRows: '1fr',
        }
      }
      return {
        gridTemplateColumns: 'repeat(' + template.cols + ', minmax(0, 1fr))',
        gridTemplateRows: 'repeat(' + template.rows + ', minmax(0, 1fr))',
      }
    },
  },
  methods: {
    runtimeFor: function (lang) {
      if (this.cellsByLang && this.cellsByLang[lang]) return this.cellsByLang[lang]
      return { text: '', lastModel: null, failed: false }
    },
  },
}
</script>

<style scoped>
.pt-grid-wrap {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  gap: 8px;
  background: #050816;
}

.pt-speaker-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.06);
  color: #E5E7EB;
  font-size: 16px;
  min-height: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.pt-speaker-strip__label {
  font-weight: 700;
  color: #94A3B8;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.pt-speaker-strip__lang {
  font-size: 12px;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.pt-speaker-strip__text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #F8FAFC;
  font-weight: 500;
}

.pt-grid {
  display: grid;
  gap: 8px;
  padding: 8px;
  min-height: 0;
}
</style>
